import { IDocumentResponse } from '../../interfaces/documentResponse';
import AppTable from '../common/AppTable';
import { ITableAction, ITableColumn } from '../../interfaces/tableProps';
import { theme } from '../../theme';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { DocumentService } from '../../services/document';
import { IDocumentsState } from './Documents';
import { useNotification } from '../../+state/useNotification';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../interfaces/errorResponse';
import { Loader } from '../common/Loader';
import { Download, FileCopy } from '@mui/icons-material';
import { downloadBlobFile } from '../../utils/downloadBlobFile';

interface IDocumentTableProps {
  revision: string;
  type: string;
  fileName: string;
  fileType: string;
  storageType: string;
  fileSize: number;
  hidden: boolean;
}

const getColumns = (isRevision: boolean): ITableColumn<keyof IDocumentTableProps>[] => [
  {
    name: 'revision',
    label: 'Revision',
    sx: isRevision
      ? undefined
      : {
        '&:hover': {
          cursor: 'pointer',
          textDecoration: 'underline',
          color: theme.palette.primary.dark
        }
      }
  },
  {
    name: 'type',
    label: 'Type',
    format: (val: string) => val?.toLowerCase()
  },
  {
    name: 'fileName',
    label: 'File Name'
  },
  {
    name: 'fileType',
    label: 'File Type'
  },
  {
    name: 'storageType',
    label: 'Storage Type',
    format: (val: string) => val?.toLowerCase()
  }
];

interface IDocumentsTableProps {
  documents: IDocumentResponse[],
  state: IDocumentsState,
  setState: Dispatch<SetStateAction<IDocumentsState>>
  revisionId?: string;
}

const DocumentsTable = ({ documents, state, setState, revisionId }: IDocumentsTableProps) => {
  const { openErrorToast } = useNotification();
  const queryClient = useQueryClient();

  const handleOpenRevisionsModal = useCallback((doc: IDocumentResponse) => setState(prev => ({
    ...prev,
    revisionId: doc.revision,
    download: null
  })), [setState]);

  const handlers: { [key: string]: (val: IDocumentResponse) => void } = useMemo(() => ({
    revision: handleOpenRevisionsModal,
  }), [handleOpenRevisionsModal]);

  const queryKey = useMemo(() => revisionId ? 'download-revision-file' : 'download-file', [revisionId]);

  const fileToDownload = useMemo(() => documents.find(d => d.revision === state.download), [documents, state.download]);
  const downloadFileHandler = useCallback(() => {
    return revisionId
      ? DocumentService.downloadDocumentRevision(state.customerId!, revisionId, state.download as string)
      : DocumentService.downloadDocument(state.customerId!, state.download as string);
  }, [revisionId, state.download, state.customerId]);

  const { isFetching } = useQuery({
    queryKey: [queryKey, state.download],
    queryFn: () => downloadFileHandler(),
    refetchOnWindowFocus: false,
    enabled: revisionId ? (!!revisionId && !!state.download) : (!!state.download && !state.revisionId),
    retry: 0,
    onSuccess: (blob) => {
      downloadBlobFile(blob, fileToDownload?.fileName);
    },
    onError: (err: AxiosError<IErrorResponse>) => {
      openErrorToast(err.response?.data.message || err.message);
    },
  });

  const handleClickDownload = useCallback((item: IDocumentResponse) => {
    if (state.download !== item.revision) {
      return setState(prev => ({
        ...prev,
        download: item.revision
      }));
    }
    queryClient.invalidateQueries([queryKey]);
  }, [state.download, queryClient, setState, queryKey]);

  const actions = useMemo<ITableAction[]>(() => {
    const baseAction = [
      { icon: <Download color={'primary'}/>, onClick: handleClickDownload, tooltip: 'Download' },
    ];
    return revisionId
      ? baseAction
      : baseAction.concat({
          icon: <FileCopy color={'primary'}/>,
          onClick: handleOpenRevisionsModal,
          tooltip: 'Open Revisions'
        },
      );
  }, [revisionId, handleClickDownload, handleOpenRevisionsModal]);

  return (
    <>
      {
        isFetching && <Loader/>
      }
      <AppTable
        data={{ list: documents, uniqueKey: 'revision' }}
        handlers={revisionId ? undefined : handlers}
        columns={getColumns(!!revisionId)}
        actions={actions}
      />
    </>
  );
};

export default DocumentsTable;